import { S3Client, PutObjectCommand, PutObjectCommandInput } from '@aws-sdk/client-s3';

export interface R2UploadResult {
  url: string;
  key: string;
  bucket: string;
  size: number;
}

export interface R2Config {
  endpoint: string;
  accessKeyId: string;
  secretAccessKey: string;
  bucketName: string;
  publicUrl?: string;
}

export class CloudflareR2Service {
  private s3Client: S3Client;
  private bucketName: string;
  private publicUrl: string;

  constructor(config: R2Config) {
    this.bucketName = config.bucketName;
    this.publicUrl = config.publicUrl || config.endpoint;

    // Initialize S3 client for Cloudflare R2
    this.s3Client = new S3Client({
      region: 'auto',
      endpoint: config.endpoint,
      credentials: {
        accessKeyId: config.accessKeyId,
        secretAccessKey: config.secretAccessKey,
      },
    });
  }

  /**
   * Generates a unique filename with timestamp and random string
   */
  private generateUniqueFilename(originalName: string, prefix: string = 'ai-touchup'): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop() || 'jpg';
    return `${prefix}/${timestamp}-${randomString}.${extension}`;
  }

  /**
   * Uploads a file to Cloudflare R2
   */
  async uploadFile(
    file: File, 
    options: {
      prefix?: string;
      customKey?: string;
      metadata?: Record<string, string>;
      userId?: string;
    } = {}
  ): Promise<R2UploadResult> {
    try {
      console.log('☁️ Starting upload to Cloudflare R2...');
      
      const key = options.customKey || this.generateUniqueFilename(file.name, options.prefix);
      
      // Prepare metadata
      const metadata: Record<string, string> = {
        'original-filename': file.name,
        'file-size': file.size.toString(),
        'upload-timestamp': new Date().toISOString(),
        ...options.metadata
      };

      if (options.userId) {
        metadata['user-id'] = options.userId;
      }

      // Convert File to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      const uploadParams: PutObjectCommandInput = {
        Bucket: this.bucketName,
        Key: key,
        Body: new Uint8Array(arrayBuffer),
        ContentType: file.type || 'image/jpeg',
        ContentLength: file.size,
        Metadata: metadata
      };

      const command = new PutObjectCommand(uploadParams);
      await this.s3Client.send(command);

      const url = `${this.publicUrl}/${key}`;
      
      console.log('✅ Upload to R2 successful');
      console.log(`🔗 File URL: ${url}`);

      return {
        url,
        key,
        bucket: this.bucketName,
        size: file.size
      };
    } catch (error) {
      console.error('❌ R2 upload failed:', error);
      throw new Error(`Failed to upload to R2: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Uploads an optimized image with specific naming for AI touchup operations
   */
  async uploadOptimizedImage(
    file: File,
    processingId: string,
    userId?: string,
    operationType: string = 'touchup'
  ): Promise<R2UploadResult> {
    const customKey = `ai-touchup/${operationType}/${processingId}-optimized.jpg`;
    
    return this.uploadFile(file, {
      customKey,
      metadata: {
        'processing-id': processingId,
        'operation-type': operationType,
        'optimized': 'true'
      },
      userId
    });
  }

  /**
   * Uploads a raw file for server-side processing
   */
  async uploadRawFileForProcessing(
    file: File,
    options: {
      prefix?: string;
      userId?: string;
    } = {}
  ): Promise<R2UploadResult> {
    const prefix = options.prefix || 'unprocessed';
    const key = this.generateUniqueFilename(file.name, prefix);

    return this.uploadFile(file, {
      customKey: key,
      userId: options.userId,
      metadata: {
        'original-filename': file.name,
        'processing-status': 'pending'
      }
    });
  }

  /**
   * Creates a service instance from environment variables
   */
  static fromEnvironment(): CloudflareR2Service {
    const endpoint = process.env.REACT_APP_CLOUDFLARE_R2_ENDPOINT;
    const accessKeyId = process.env.REACT_APP_CLOUDFLARE_R2_ACCESS_KEY_ID;
    const secretAccessKey = process.env.REACT_APP_CLOUDFLARE_R2_SECRET_ACCESS_KEY;
    const bucketName = process.env.REACT_APP_CLOUDFLARE_R2_BUCKET_NAME;
    const publicUrl = process.env.REACT_APP_CLOUDFLARE_R2_PUBLIC_URL;

    if (!endpoint || !accessKeyId || !secretAccessKey || !bucketName) {
      throw new Error('Missing required Cloudflare R2 environment variables. Please check your configuration.');
    }

    return new CloudflareR2Service({
      endpoint,
      accessKeyId,
      secretAccessKey,
      bucketName,
      publicUrl
    });
  }
} 